import type { Document } from '@contentful/rich-text-types';
import type { ContentImageEntry } from 'api/contentful/contentTypes.cloverdotcom';
import { BYOStepId, FeatureSection, LinkVariant } from 'api/contentful/contentTypes.cloverdotcom';
import cx from 'classnames';
import { useBuildYourOwn } from 'features/build-your-own/hooks/useBuildYourOwn';
import React, { FC } from 'react';
import { ItemActionButton } from './ItemActionButton';
import styles from './ItemInfoAndActions.module.scss';
import { ItemOutOfStockButton } from './ItemOutOfStockButton';
import { SproutButton } from 'components/Button/SproutButton';
import { RichBlurb } from 'components/RichBlurb/RichBlurb';
import { ButtonVariant } from '@clover-web-lib-sprout/button';
import { ItemFeatureDetails } from './ItemFeatureDetails';
import { FlatEntry } from 'services/cms/flattenContentful';

export type ActionButton = {
  text?: string;
  imageEntry?: ContentImageEntry;
};

export const scrollIntoView = (button: HTMLButtonElement) => {
  button.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
    inline: 'center',
  });
};

interface IInfoAndActionProps {
  itemKey?: string;
  isAddButtonShown?: boolean;
  areProductsOutOfStock?: boolean;
  primaryActionButton?: ActionButton;
  secondaryActionButton?: ActionButton;
  itemQuantity?: number;
  isMultipleQuantityAllowed?: boolean;
  handleActionClick?: (isRemoval?: boolean) => void;
  isSelected?: boolean;
  includedFeatures?: FlatEntry<FeatureSection>[];
  showMoreInfo?: boolean;
  selectButtonText?: string;
  selectedButtonText?: string;
  moreInfo?: Document;
}

export const ItemInfoAndActions: FC<IInfoAndActionProps> = ({
  itemKey,
  isAddButtonShown,
  areProductsOutOfStock,
  primaryActionButton,
  secondaryActionButton,
  itemQuantity = 0,
  isMultipleQuantityAllowed = false,
  handleActionClick = () => null,
  isSelected,
  includedFeatures,
  showMoreInfo,
  selectButtonText,
  selectedButtonText,
  moreInfo,
}) => {
  const { currentStepData, currentStepId } = useBuildYourOwn();

  if (!currentStepData) {
    return null;
  }

  const showSecondaryAction = currentStepId === BYOStepId.DEVICES || currentStepId === BYOStepId.ACCESSORIES;
  const isSecondaryActionEnabled = isMultipleQuantityAllowed && itemQuantity > 0 && !!secondaryActionButton;

  const areProductsOutOfStockInDevicesAndAccessoriesStep =
    (currentStepId === BYOStepId.DEVICES || currentStepId === BYOStepId.ACCESSORIES) && areProductsOutOfStock;

  const handleSelectedCard = () => {
    return isSelected
      ? { buttonText: selectedButtonText, buttonVariant: LinkVariant.PRIMARY }
      : { buttonText: selectButtonText, buttonVariant: LinkVariant.SECONDARY };
  };

  const { buttonText, buttonVariant } = handleSelectedCard();
  const isPosNeedsStep = currentStepId === BYOStepId.POS_NEEDS;
  const isDeviceOrAccessoriesStep =
    currentStepId === BYOStepId.DEVICES || currentStepId === BYOStepId.ACCESSORIES;
  const isNotBusinessOfferingStep = currentStepId !== BYOStepId.BUSINESS_OFFERING;

  return (
    <>
      <div
        className={cx(isNotBusinessOfferingStep ? styles.buttonContainer2 : styles.buttonContainer, {
          [styles.buttonContainer2ItemIconOnly]: !includedFeatures,
        })}
      >
        {showMoreInfo && <RichBlurb document={moreInfo} />}
        {includedFeatures && !moreInfo && (
          <div className={cx(styles.moreInfo, { [styles.moreInfoVisible]: showMoreInfo })}>
            <ItemFeatureDetails features={includedFeatures} />
          </div>
        )}
        {isAddButtonShown &&
          (areProductsOutOfStockInDevicesAndAccessoriesStep ? (
            <ItemOutOfStockButton />
          ) : (
            <div
              className={cx(styles.actionsButtonContainer, {
                [styles.actionsButtonContainerPosNeedsStep]: isPosNeedsStep,
              })}
            >
              {showSecondaryAction && (
                <>
                  <ItemActionButton
                    itemKey={itemKey}
                    actionButton={secondaryActionButton!}
                    handleActionClick={() => handleActionClick(true)}
                    isDisabled={!isSecondaryActionEnabled}
                    handleFocus={e => scrollIntoView(e.currentTarget)}
                  />
                  <div>
                    <span>{itemQuantity}</span>
                  </div>
                </>
              )}
              {isPosNeedsStep && (
                <SproutButton
                  variant={buttonVariant as ButtonVariant}
                  onClick={() => handleActionClick()}
                  className={styles.sproutButton}
                  onFocus={e => scrollIntoView(e.currentTarget)}
                >
                  {buttonText}
                </SproutButton>
              )}
              {primaryActionButton && isDeviceOrAccessoriesStep && (
                <ItemActionButton
                  itemKey={itemKey}
                  actionButton={primaryActionButton}
                  handleActionClick={() => handleActionClick()}
                  handleFocus={e => scrollIntoView(e.currentTarget)}
                />
              )}
            </div>
          ))}
      </div>
    </>
  );
};
